import { memo } from "react";
import { CheckCircleIcon } from "@";

/**
 * Provider selector — a row of large pill cards. Single-select.
 * Each card shows: provider logo/initial, name, short description,
 * an "active" status badge when selected, and a radio indicator.
 *
 * @param {{
 *   providers: Array<{ key: string, name: string, description?: string, logo?: string, accent?: string }>,
 *   value: string,
 *   onChange: (key: string) => void,
 *   disabled?: boolean,
 * }} props
 */
const ProviderSelector = ({ providers, value, onChange, disabled }) => {
  return (
    <div
      role="radiogroup"
      aria-label="مزوّد الخدمة"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
    >
      {providers.map((p) => {
        const isSelected = value === p.key;
        return (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(p.key)}
            className={[
              "group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border p-3.5 text-start transition-all duration-200",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 focus-visible:ring-offset-2",
              isSelected
                ? "border-purple-300 bg-gradient-to-bl from-purple-50 via-white to-white shadow-sm shadow-purple-900/5 dark:border-purple-700/60 dark:from-purple-950/30 dark:via-gray-800 dark:to-gray-800"
                : "border-gray-200 bg-white hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-sm hover:shadow-purple-900/5 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-purple-700/40",
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
            ].join(" ")}
          >
            {/* Logo / initial */}
            <span
              className={[
                "relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-black tracking-tight ring-1 transition-colors",
                isSelected
                  ? "bg-white text-purple-900 ring-purple-200 dark:bg-gray-900 dark:text-purple-300 dark:ring-purple-700/50"
                  : "bg-gray-50 text-gray-500 ring-gray-200 dark:bg-gray-900/40 dark:text-gray-400 dark:ring-gray-700",
              ].join(" ")}
            >
              {p.logo ? (
                <img
                  src={p.logo}
                  alt=""
                  className="h-7 w-7 object-contain"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span aria-hidden>{p.name.slice(0, 2)}</span>
              )}
            </span>

            {/* Name + description */}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex items-center gap-2">
                <span
                  className={[
                    "truncate text-[15px] font-bold tracking-tight",
                    isSelected
                      ? "text-purple-900 dark:text-purple-200"
                      : "text-gray-800 dark:text-gray-200",
                  ].join(" ")}
                >
                  {p.name}
                </span>
                {isSelected && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-800/50">
                    <span
                      aria-hidden
                      className="h-1 w-1 rounded-full bg-emerald-500"
                    />
                    مفعل
                  </span>
                )}
              </span>
              {p.description && (
                <span className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                  {p.description}
                </span>
              )}
            </span>

            {/* Radio indicator */}
            <span
              aria-hidden
              className={[
                "relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-1 transition-all",
                isSelected
                  ? "bg-purple-700 text-white ring-purple-700 dark:bg-purple-500 dark:ring-purple-500"
                  : "bg-white text-transparent ring-gray-300 group-hover:ring-purple-300 dark:bg-gray-800 dark:ring-gray-600",
              ].join(" ")}
            >
              <CheckCircleIcon className="h-4 w-4" strokeWidth={3} />
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default memo(ProviderSelector);
